/**
 * Phase N — /company · investor-grade one-pager
 */
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: "Beyond Valencia",
  alternateName: "Beyond Valencia by Ivy League Consulting",
  description: "The national platform for transfer strategy, educational mobility, and second-chance admissions.",
  url: "https://ivyleagueconsulting.org/company",
  areaServed: { "@type": "Country", name: "United States" },
};

function Section({ eyebrow, title, children, id, testId }) {
  return (
    <section id={id} data-testid={testId} className="border-b border-stone/20 py-14 sm:py-16">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{eyebrow}</div>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-6 max-w-3xl text-ink-soft leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

export default function Company() {
  return (
    <div className="min-h-screen bg-bone" data-testid="company-page">
      <SEO
        title="Company | Beyond Valencia"
        description="Mission, vision, brand positioning, and roadmap for Beyond Valencia — the national platform for transfer strategy, educational mobility, and second-chance admissions."
        canonical="/company"
        ogType="article"
        jsonLd={ORG_JSONLD}
      />
      <TopNav />

      <main>
        <section className="bg-ivy text-bone" data-testid="company-hero">
          <div className="max-w-5xl mx-auto px-5 py-16 sm:py-24">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Company</div>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="company-h1">
              Beyond Valencia
            </h1>
            <p className="mt-6 max-w-2xl text-bone-dim text-lg leading-relaxed">
              The national platform for transfer strategy, educational mobility, and second-chance admissions.
            </p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-5">
          <Section eyebrow="Mission" title="Educational mobility, honestly delivered." id="mission" testId="company-mission">
            <p>Beyond Valencia is a national education company built around a simple conviction: where you begin should never determine where you finish. We are the transfer intelligence platform for students who chose community college, took a non-traditional path, or need a second chance at the college admissions process — and want ambitious, honest guidance to reach the university that actually fits them.</p>
            <p>We operate at the intersection of admissions strategy, educational-mobility research, and modern technology to make that path visible and reachable.</p>
          </Section>

          <Section eyebrow="Vision" title="A country where the transfer path is a first-class option." id="vision" testId="company-vision">
            <p>A country in which the transfer pathway is treated as a first-class route into any US university, and every ambitious community-college student has access to the same admissions intelligence historically reserved for freshman applicants at elite feeder schools.</p>
          </Section>

          <Section eyebrow="The problem" title="Transfer intelligence is scarce, uneven, and locked away." id="problem" testId="company-problem">
            <p>Millions of students in the United States begin their undergraduate education outside a four-year university — at community colleges, through non-traditional pathways, after gap years, or after a rejected freshman cycle. For most of them, the transfer route is under-explained, under-supported, and treated as a lesser path even when the data says the opposite.</p>
            <p>The admissions intelligence that fills expensive private consulting firms is almost never designed with these students in mind. Requirement graphs, articulation rules, tracking sheets, GPA thresholds, and elite-transfer strategy are scattered across catalogs and PDFs that most students never see.</p>
          </Section>

          <Section eyebrow="The solution" title="One verified graph. Ambitious strategy. Honest delivery." id="solution" testId="company-solution">
            <p>Beyond Valencia builds the transfer intelligence layer: a verified requirement graph that maps community-college coursework to destination-university programs, honest diagnostics that show a student exactly where they stand, and human strategy from a multidisciplinary team of educational strategists, transfer specialists, and technology partners.</p>
            <p>Every prerequisite, GPA threshold, and articulation claim on the platform maps to a verified source URL. We do not fabricate admissions data. We do not invent course codes. We do not sell hope.</p>
          </Section>

          <Section eyebrow="Differentiation" title="What makes Beyond Valencia different." id="difference" testId="company-difference">
            <p>Unlike traditional admissions consultancies that focus on freshman applicants at elite feeder schools, Beyond Valencia is built specifically for the transfer pathway — with a verified requirement graph, programmatic pair pages for the most common community-college → four-year transitions, and a formal partnership with Ivy League Consulting for elite and Ivy destinations. Together we cover any US university transfer pathway.</p>
          </Section>

          <Section eyebrow="Brand positioning" title="Harvard Kennedy School × McKinsey Insights × modern education technology." id="positioning" testId="company-positioning">
            <p>The tone is that of Harvard Kennedy School — serious, mission-driven, rooted in research. The clarity is that of McKinsey Insights — structured frameworks, defensible claims, no filler. The delivery is modern education technology — a tool a student can actually use, not a PDF locked behind a paywall.</p>
          </Section>

          <Section eyebrow="Core brand message" title="Three ideas — plus one partnership." id="core-message" testId="company-core-message">
            <ol className="list-decimal list-inside space-y-2">
              <li>Your starting point is not your ceiling.</li>
              <li>Community college can be one of the smartest strategic pathways in American higher education.</li>
              <li>Ambitious students deserve ambitious guidance regardless of where they begin.</li>
              <li>Beyond Valencia + <Link to="/partners/ivy-league-consulting" className="text-ivy underline hover:text-brass">Ivy League Consulting</Link> together provide end-to-end coverage — Florida-anchored transfer engine meets elite / Ivy admissions strategy.</li>
            </ol>
          </Section>

          <Section eyebrow="The promise" title="What we owe every student who trusts us." id="promise" testId="company-promise">
            <p>Every claim on this platform is defensible. Every source URL is real and reachable. Every AI-generated recommendation is grounded in verified data or clearly labeled as illustrative. Every recommendation to seek help outside our platform — including our own partner — is made honestly because it serves the student, not our funnel.</p>
          </Section>

          <Section eyebrow="Where we're going" title="Early journey. Ambitious roadmap." id="roadmap" testId="company-roadmap">
            <p>Beyond Valencia is early in its journey. Our roadmap includes expanding institutional partnerships, publishing original transfer-outcomes research, growing our adviser network, and building the first comprehensive open catalog of Florida transfer articulation.</p>
            <p>Investors and institutional partners interested in this trajectory can reach us at{" "}
              <a href="mailto:partnerships@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="company-partnerships-email">partnerships@beyondvalencia.com</a>.
              {/* TODO: swap for real partnerships email when founder provides */}
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
